import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  CalendarDays,
  LogIn,
  Map as MapIcon,
  MapPin,
  Medal,
  UserPlus,
  Zap,
} from "lucide-react";
import logo from "assets/logo/logo.png";
import { getAllLocations } from "services/locationService";
import LocationAddress from "./LocationAddress";

const FEATURES = [
  {
    icon: CalendarDays,
    title: "Un tournoi chaque semaine",
    text: "Des hebdos ont lieu toutes les semaines, dans plusieurs villes du Québec. Trouvez une table près de chez vous.",
  },
  {
    icon: Zap,
    title: "Inscription en un clic",
    text: "Réservez votre place en quelques secondes, voyez qui est inscrit et choisissez votre partenaire.",
  },
  {
    icon: Medal,
    title: "Classements et podiums",
    text: "Suivez vos matchs en direct, votre classement et les résultats de chaque semaine.",
  },
];

// Players per rod on a standard table: goalie, defense, midfield, attack
const RODS = [
  { team: "home", players: 1 },
  { team: "home", players: 2 },
  { team: "away", players: 3 },
  { team: "home", players: 5 },
  { team: "away", players: 5 },
  { team: "home", players: 3 },
  { team: "away", players: 2 },
  { team: "away", players: 1 },
];

// Top view of a foosball table, purely decorative
const FoosballTable = () => (
  <svg viewBox="0 0 260 160" className="w-full h-auto drop-shadow-2xl" aria-hidden="true">
    <rect x="4" y="4" width="252" height="152" rx="10" className="fill-slate-800 stroke-slate-600" strokeWidth="3" />
    <rect x="16" y="14" width="228" height="132" rx="4" className="fill-emerald-900/60 stroke-emerald-200/30" strokeWidth="1.5" />
    <line x1="130" y1="14" x2="130" y2="146" className="stroke-emerald-200/30" strokeWidth="1.5" />
    <circle cx="130" cy="80" r="18" className="fill-none stroke-emerald-200/30" strokeWidth="1.5" />
    <rect x="10" y="62" width="6" height="36" rx="1" className="fill-slate-950" />
    <rect x="244" y="62" width="6" height="36" rx="1" className="fill-slate-950" />
    {RODS.map((rod, i) => {
      const x = 32 + i * 28;
      const spacing = 132 / (rod.players + 1);
      return (
        <g key={i}>
          <line x1={x} y1="0" x2={x} y2="160" className="stroke-slate-400" strokeWidth="2.5" strokeLinecap="round" />
          {Array.from({ length: rod.players }, (_, p) => (
            <rect
              key={p}
              x={x - 5}
              y={14 + spacing * (p + 1) - 7}
              width="10"
              height="14"
              rx="3"
              className={rod.team === "home" ? "fill-amber-400" : "fill-sky-400"}
            />
          ))}
        </g>
      );
    })}
    <motion.circle
      r="5"
      className="fill-white"
      initial={{ cx: 130, cy: 80 }}
      animate={{ cx: [130, 172, 104, 214, 130], cy: [80, 52, 112, 70, 80] }}
      transition={{ duration: 7, repeat: Infinity, ease: "easeInOut" }}
    />
  </svg>
);

const fadeUp = (delay = 0) => ({
  initial: { opacity: 0, y: 16 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.5, delay },
});

// Shown instead of the tournaments when nobody is logged in: tournament data
// (players, registrations, rankings) is only readable by logged-in users.
const LoginInvite = ({ location, locationId, addressPoint }) => {
  const [hebdos, setHebdos] = useState([]);

  useEffect(() => {
    getAllLocations()
      .then((all) => setHebdos(all.filter((loc) => loc.active === true)))
      .catch((error) => console.error("Error fetching locations:", error));
  }, []);

  const towns = [...new Set(hebdos.map((loc) => loc.town).filter(Boolean))].sort(
    (a, b) => a.localeCompare(b, "fr")
  );
  const image = location?.image?.trim();
  const redirect = { from: `/locations/${locationId}` };

  return (
    <div className="flex-1 w-full max-w-6xl mx-auto px-4 py-6 md:py-10 space-y-10">
      {/* Hero */}
      <section className="relative overflow-hidden rounded-2xl border border-slate-800 bg-slate-900">
        {image && (
          <img
            src={image}
            alt=""
            className="absolute inset-0 h-full w-full object-cover opacity-25"
            onError={(e) => { e.target.style.display = "none"; }}
          />
        )}
        <div className="absolute inset-0 bg-gradient-to-br from-slate-950 via-slate-950/85 to-amber-900/40" />

        <div className="relative grid gap-10 px-6 py-10 md:grid-cols-[1.2fr_1fr] md:items-center md:px-12 md:py-16">
          <div>
            <motion.span
              {...fadeUp(0)}
              className="inline-flex items-center gap-2 rounded-full bg-amber-400/10 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-amber-300 ring-1 ring-amber-400/30"
            >
              <span className="h-2 w-2 rounded-full bg-amber-400 animate-pulse" />
              Tournoi hebdomadaire
            </motion.span>

            <motion.h1
              {...fadeUp(0.1)}
              className="mt-5 text-4xl font-extrabold leading-tight text-white md:text-5xl"
            >
              Votre place à la table <span className="text-amber-400">vous attend.</span>
            </motion.h1>

            <motion.p {...fadeUp(0.2)} className="mt-5 text-lg text-slate-300">
              {location ? (
                <>
                  <span className="font-semibold text-white">{location.name}</span>
                  {location.town && <> à {location.town}</>} accueille un tournoi de
                  babyfoot chaque semaine.
                </>
              ) : (
                "Un tournoi de babyfoot a lieu ici chaque semaine."
              )}{" "}
              Connectez-vous pour voir le prochain tournoi, la liste des inscrits et
              réserver votre place.
            </motion.p>

            {addressPoint && (
              <motion.div {...fadeUp(0.25)} className="mt-4">
                <LocationAddress point={addressPoint} className="text-base" />
              </motion.div>
            )}

            <motion.div {...fadeUp(0.3)} className="mt-8 flex flex-col gap-3 sm:flex-row">
              <Link
                to="/login"
                state={redirect}
                className="inline-flex items-center justify-center gap-2 rounded-lg bg-amber-500 px-6 py-3 text-lg font-bold text-slate-950 shadow-lg shadow-amber-500/20 transition hover:bg-amber-400"
              >
                <LogIn size={20} /> Se connecter
              </Link>
              <Link
                to="/signup"
                className="inline-flex items-center justify-center gap-2 rounded-lg border border-slate-500 px-6 py-3 text-lg font-semibold text-white transition hover:border-white hover:bg-white/5"
              >
                <UserPlus size={20} /> Créer un compte
              </Link>
            </motion.div>
            <motion.p {...fadeUp(0.35)} className="mt-3 text-sm text-slate-400">
              Pas encore de compte? La création est gratuite et ne prend qu'une minute.
            </motion.p>
          </div>

          {/* Table illustration signed by the federation (logo only on mobile) */}
          <div className="mx-auto flex w-full max-w-sm flex-col items-center border-t border-slate-800 pt-8 md:border-0 md:pt-0">
            <motion.div
              initial={{ opacity: 0, scale: 0.94, rotate: -4 }}
              animate={{ opacity: 1, scale: 1, rotate: -4 }}
              transition={{ duration: 0.7, delay: 0.2 }}
              className="hidden w-full md:block"
            >
              <FoosballTable />
            </motion.div>
            <motion.div
              {...fadeUp(0.5)}
              className="flex flex-col items-center gap-2 text-center md:mt-10"
            >
              <img src={logo} alt="FQST" className="h-9 w-auto md:h-11" />
              <p className="text-[11px] font-medium uppercase tracking-[0.2em] text-slate-400">
                Fédération québécoise de soccer sur table
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Why sign in */}
      <section className="grid gap-4 md:grid-cols-3">
        {FEATURES.map(({ icon: Icon, title, text }, i) => (
          <motion.div
            key={title}
            {...fadeUp(0.4 + i * 0.1)}
            className="rounded-xl border border-slate-800 bg-slate-900/60 p-6"
          >
            <div className="mb-4 inline-flex h-11 w-11 items-center justify-center rounded-lg bg-amber-400/10 text-amber-400">
              <Icon size={22} />
            </div>
            <h2 className="text-lg font-semibold text-white">{title}</h2>
            <p className="mt-2 text-sm leading-relaxed text-slate-400">{text}</p>
          </motion.div>
        ))}
      </section>

      {/* Other hebdos */}
      {hebdos.length > 0 && (
        <motion.section
          {...fadeUp(0.6)}
          className="rounded-2xl border border-slate-800 bg-slate-900/60 p-6 md:p-8"
        >
          <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
            <div>
              <h2 className="text-2xl font-bold text-white">Des hebdos partout au Québec</h2>
              <p className="mt-1 text-slate-400">
                <span className="font-semibold text-amber-400">
                  {hebdos.length} {hebdos.length > 1 ? "tournois hebdomadaires" : "tournoi hebdomadaire"}
                </span>{" "}
                dans{" "}
                <span className="font-semibold text-amber-400">
                  {towns.length} {towns.length > 1 ? "villes" : "ville"}
                </span>
                . Trouvez celui qui vous convient.
              </p>
            </div>
            <Link
              to="/carte"
              className="inline-flex items-center gap-2 self-start rounded-lg bg-slate-800 px-4 py-2 text-sm font-semibold text-white transition hover:bg-slate-700 md:self-auto"
            >
              <MapIcon size={16} /> Voir la carte
            </Link>
          </div>

          <div className="mt-6 flex flex-wrap gap-2">
            {hebdos
              .slice()
              .sort((a, b) => (a.town || "").localeCompare(b.town || "", "fr"))
              .map((loc) => {
                const current = loc.id === locationId;
                return (
                  <Link
                    key={loc.id}
                    to={`/locations/${loc.id}`}
                    aria-current={current ? "page" : undefined}
                    className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm transition ${
                      current
                        ? "bg-amber-500 font-semibold text-slate-950"
                        : "bg-slate-800 text-slate-200 hover:bg-slate-700"
                    }`}
                  >
                    <MapPin size={14} />
                    {loc.name}
                    {loc.table && <span className="opacity-70">· {loc.table}</span>}
                    {loc.town && <span className="opacity-70">· {loc.town}</span>}
                  </Link>
                );
              })}
          </div>
        </motion.section>
      )}
    </div>
  );
};

export default LoginInvite;
